import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaRedoAlt,
  FaUserTie,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import { readStoredAdminUser } from './adminAccessControl';
import './PTScheduleManager.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'requested', label: 'Requested' },
  { value: 'on_schedule', label: 'On Schedule' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

const STATUS_META = {
  requested: { label: 'Requested', badge: 'warning', className: 'requested' },
  on_schedule: { label: 'On Schedule', badge: 'info', className: 'on-schedule' },
  scheduled: { label: 'Scheduled', badge: 'primary', className: 'scheduled' },
  completed: { label: 'Completed', badge: 'success', className: 'completed' },
  cancelled: { label: 'Cancelled', badge: 'secondary', className: 'cancelled' },
  rescheduled: { label: 'Rescheduled', badge: 'dark', className: 'rescheduled' },
};

const WEEKDAY_LABELS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

const normalizeStatus = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const resolveStatusMeta = value => STATUS_META[normalizeStatus(value)] || STATUS_META.scheduled;

const padNumber = value => String(value).padStart(2, '0');

const createMonthCursor = baseDate =>
  new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);

const formatISODate = date =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;

const parseISODate = value => {
  const [year, month, day] = String(value || '')
    .split('-')
    .map(item => Number(item));

  if (!year || !month || !day) {
    return null;
  }

  const result = new Date(year, month - 1, day);
  return Number.isNaN(result.getTime()) ? null : result;
};

const formatMonthLabel = value =>
  value.toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  });

const formatLongDate = value => {
  const parsed = parseISODate(value);
  if (!parsed) {
    return '-';
  }

  return parsed.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const isSameMonth = (left, right) =>
  left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

const buildCalendarDays = monthCursor => {
  const gridStart = new Date(monthCursor);
  gridStart.setDate(1 - monthCursor.getDay());

  return Array.from({ length: 42 }, (_, index) =>
    new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + index
    )
  );
};

const monthRange = monthCursor => ({
  from: formatISODate(monthCursor),
  to: formatISODate(
    new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0)
  ),
});

const sortScheduleItems = items =>
  [...items].sort((left, right) => {
    const timeCompare = String(left.schedule_time || '').localeCompare(
      String(right.schedule_time || '')
    );
    if (timeCompare !== 0) {
      return timeCompare;
    }

    const ptCompare = String(left.pt_name || '').localeCompare(
      String(right.pt_name || '')
    );
    if (ptCompare !== 0) {
      return ptCompare;
    }

    return String(left.client_name || '').localeCompare(
      String(right.client_name || '')
    );
  });

const createFilterState = () => ({
  ptName: '',
  clientName: '',
  status: '',
});

const isApiSuccess = data =>
  data &&
  (data.error === 0 || data.error === '0' || typeof data.error === 'undefined');

const resolveRecommendationRiskMeta = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'rendah':
    case 'low':
      return { label: 'Rendah', badge: 'success' };
    case 'tinggi':
    case 'high':
      return { label: 'Tinggi', badge: 'danger' };
    default:
      return { label: 'Sedang', badge: 'warning' };
  }
};

const resolveRecommendationConfidenceLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'tinggi':
    case 'high':
      return 'Tinggi';
    case 'rendah':
    case 'low':
      return 'Rendah';
    default:
      return 'Sedang';
  }
};

function PTScheduleManager() {
  const token = useMemo(() => localStorage.getItem('sessionToken') || '', []);
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const fallbackOutletId = useMemo(
    () => Number(currentUser?.outlet_id || currentUser?.OutletID || 0) || 0,
    [currentUser]
  );
  const aiOutletLocked = useMemo(
    () => fallbackOutletId > 0 && String(currentUser?.actor_type || currentUser?.actorType || '').trim().toLowerCase() !== 'admin',
    [currentUser, fallbackOutletId]
  );

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const [monthCursor, setMonthCursor] = useState(() =>
    createMonthCursor(new Date())
  );
  const [filterForm, setFilterForm] = useState(createFilterState);
  const [filters, setFilters] = useState(createFilterState);
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() =>
    formatISODate(new Date())
  );
  const [loadedRange, setLoadedRange] = useState(() => monthRange(createMonthCursor(new Date())));
  const [aiForm, setAiForm] = useState(() => ({
    outletId: fallbackOutletId ? String(fallbackOutletId) : '',
    clientId: 0,
    clientName: '',
    preferredDate: formatISODate(new Date()),
    preferredTime: '',
  }));
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const parseApiResponse = useCallback(async response => {
    const raw = await response.text();
    let data = {};

    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch (error) {
        data = { message: raw };
      }
    }

    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data;
  }, []);

  const fetchSchedules = useCallback(async () => {
    if (!token) {
      return;
    }

    const range = monthRange(monthCursor);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}pt-schedule/list`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 1000,
          offset: 0,
          date_from: range.from,
          date_to: range.to,
          pt_name: filters.ptName.trim(),
          client_name: filters.clientName.trim(),
          status: filters.status,
        }),
      });

      const data = await parseApiResponse(response);

      if (!isApiSuccess(data)) {
        throw new Error(data.message || 'Gagal mengambil jadwal session');
      }

      const results = Array.isArray(data.results) ? data.results : [];
      setSchedules(sortScheduleItems(results));
      setLoadedRange(range);
    } catch (error) {
      console.error(error);
      setSchedules([]);
      Swal.fire('Error', error.message || 'Gagal mengambil jadwal session', 'error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filters, monthCursor, parseApiResponse, token]);

  const fetchAIScheduleRecommendation = useCallback(async nextForm => {
    const clientName = String(nextForm.clientName || '').trim();
    const clientId = Number(nextForm.clientId || 0);

    if (!clientId && !clientName) {
      Swal.fire('Info', 'Isi nama client atau pilih dari agenda terlebih dahulu.', 'info');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}pt-schedule/recommendation`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          outlet_id: Number(nextForm.outletId || 0),
          client_id: clientId,
          client_name: clientName,
          preferred_date: nextForm.preferredDate || selectedDate,
          preferred_time: nextForm.preferredTime || '',
          lookahead_days: 14,
          limit: 3,
        }),
      });

      const data = await parseApiResponse(response);
      if (!isApiSuccess(data) || !data?.data) {
        throw new Error(data.message || 'Gagal membuat rekomendasi AI scheduling');
      }

      setAiRecommendation(data.data);
    } catch (error) {
      console.error(error);
      setAiRecommendation(null);
      Swal.fire('Error', error.message || 'Gagal membuat rekomendasi AI scheduling', 'error');
    } finally {
      setAiLoading(false);
    }
  }, [authHeaders, parseApiResponse, selectedDate]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  useEffect(() => {
    const today = new Date();
    const fallbackDate = isSameMonth(monthCursor, today)
      ? formatISODate(today)
      : formatISODate(monthCursor);

    setSelectedDate(previousValue => {
      const parsed = parseISODate(previousValue);
      if (parsed && isSameMonth(parsed, monthCursor)) {
        return previousValue;
      }
      return fallbackDate;
    });
  }, [monthCursor]);

  useEffect(() => {
    setAiForm(previousValue => ({
      ...previousValue,
      preferredDate: selectedDate || previousValue.preferredDate,
    }));
  }, [selectedDate]);

  const calendarDays = useMemo(() => buildCalendarDays(monthCursor), [monthCursor]);

  const schedulesByDate = useMemo(() => {
    const grouped = new Map();

    schedules.forEach(item => {
      const key = String(item.schedule_date || '').trim();
      if (!key) {
        return;
      }

      const existing = grouped.get(key) || [];
      existing.push(item);
      grouped.set(key, existing);
    });

    grouped.forEach((items, key) => {
      grouped.set(key, sortScheduleItems(items));
    });

    return grouped;
  }, [schedules]);

  const selectedDateSchedules = useMemo(
    () => schedulesByDate.get(selectedDate) || [],
    [schedulesByDate, selectedDate]
  );

  const summary = useMemo(() => {
    const ptNames = new Set();
    const clientNames = new Set();
    const statusCounts = Object.keys(STATUS_META).reduce((result, key) => {
      result[key] = 0;
      return result;
    }, {});

    schedules.forEach(item => {
      const ptName = String(item.pt_name || '').trim();
      const clientName = String(item.client_name || '').trim();
      const status = normalizeStatus(item.status);

      if (ptName) {
        ptNames.add(ptName);
      }
      if (clientName) {
        clientNames.add(clientName);
      }

      if (Object.prototype.hasOwnProperty.call(statusCounts, status)) {
        statusCounts[status] += 1;
      } else {
        statusCounts.scheduled += 1;
      }
    });

    const activeSessions =
      statusCounts.requested +
      statusCounts.on_schedule +
      statusCounts.scheduled +
      statusCounts.rescheduled;

    return {
      totalSchedules: schedules.length,
      activePT: ptNames.size,
      activeClients: clientNames.size,
      completed: statusCounts.completed,
      activeSessions,
      statusCounts,
    };
  }, [schedules]);

  const selectedDateRemainingSessions = useMemo(
    () => {
      const balanceByClient = new Map();

      selectedDateSchedules.forEach(item => {
        const clientID = Number(item.client_id || 0);
        const clientName = String(item.client_name || '').trim().toLowerCase();
        const clientKey = clientID > 0 ? `id:${clientID}` : `name:${clientName}`;
        const nextRemaining = Number(item.remaining_sessions || 0);
        const previousRemaining = balanceByClient.get(clientKey);

        if (typeof previousRemaining === 'undefined' || nextRemaining > previousRemaining) {
          balanceByClient.set(clientKey, nextRemaining);
        }
      });

      return [...balanceByClient.values()].reduce(
        (total, value) => total + Number(value || 0),
        0
      );
    },
    [selectedDateSchedules]
  );

  const handleFilterSubmit = event => {
    event.preventDefault();
    setFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    const nextFilters = createFilterState();
    setFilterForm(nextFilters);
    setFilters(nextFilters);
  };

  const handleShiftMonth = step => {
    setMonthCursor(previousValue =>
      new Date(
        previousValue.getFullYear(),
        previousValue.getMonth() + step,
        1
      )
    );
  };

  const handleJumpToCurrentMonth = () => {
    const today = new Date();
    setMonthCursor(createMonthCursor(today));
    setSelectedDate(formatISODate(today));
  };

  const handleAIFormSubmit = event => {
    event.preventDefault();
    fetchAIScheduleRecommendation(aiForm);
  };

  const handleAnalyzeSchedule = item => {
    const nextForm = {
      outletId: String(item?.outlet_id || aiForm.outletId || ''),
      clientId: Number(item?.client_id || 0),
      clientName: String(item?.client_name || ''),
      preferredDate: String(item?.schedule_date || selectedDate || aiForm.preferredDate || ''),
      preferredTime: String(item?.schedule_time || ''),
    };

    setAiForm(nextForm);
    fetchAIScheduleRecommendation(nextForm);
  };

  const todayLabel = formatISODate(new Date());

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4 pb-5">
        <AdminPageHeader
          icon={FaCalendarAlt}
          eyebrow="Session schedule"
          title="Private Session Calendar"
          description="Modul ini menampilkan agenda private session per bulan dalam bentuk kalender. Pengaturan layanan dilakukan dari Program Setup > Private Session Setup."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Session Schedule' },
          ]}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-secondary" onClick={handleJumpToCurrentMonth}>
                <FaCalendarAlt className="me-2" />
                Bulan Ini
              </Button>
            </div>
          )}
        />

        <Row className="g-3 mb-4">
          <Col md={6} xl={3}>
            <Card className="pt-schedule-calendar__summary-card shadow-sm border-0 h-100">
              <Card.Body>
                <div className="pt-schedule-calendar__summary-label">Total sesi</div>
                <div className="pt-schedule-calendar__summary-value">
                  {summary.totalSchedules}
                </div>
                <div className="pt-schedule-calendar__summary-note">
                  Jadwal dalam rentang {loadedRange.from} s.d. {loadedRange.to}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="pt-schedule-calendar__summary-card shadow-sm border-0 h-100">
              <Card.Body>
                <div className="pt-schedule-calendar__summary-icon">
                  <FaUserTie />
                </div>
                <div className="pt-schedule-calendar__summary-label">Staff aktif</div>
                <div className="pt-schedule-calendar__summary-value">
                  {summary.activePT}
                </div>
                <div className="pt-schedule-calendar__summary-note">
                  {summary.activeClients} client terjadwal bulan ini
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="pt-schedule-calendar__summary-card shadow-sm border-0 h-100">
              <Card.Body>
                <div className="pt-schedule-calendar__summary-icon">
                  <FaClock />
                </div>
                <div className="pt-schedule-calendar__summary-label">Aktif / pending</div>
                <div className="pt-schedule-calendar__summary-value">
                  {summary.activeSessions}
                </div>
                <div className="pt-schedule-calendar__summary-note">
                  Requested, scheduled, on schedule, dan rescheduled
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} xl={3}>
            <Card className="pt-schedule-calendar__summary-card shadow-sm border-0 h-100">
              <Card.Body>
                <div className="pt-schedule-calendar__summary-icon">
                  <FaCheckCircle />
                </div>
                <div className="pt-schedule-calendar__summary-label">Completed</div>
                <div className="pt-schedule-calendar__summary-value">
                  {summary.completed}
                </div>
                <div className="pt-schedule-calendar__summary-note">
                  Sesi yang selesai pada bulan aktif
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="pt-schedule-calendar__calendar-card shadow-sm border-0">
          <Card.Body>
            <div className="pt-schedule-calendar__toolbar">
              <div className="pt-schedule-calendar__month-nav">
                <Button
                  variant="outline-secondary"
                  onClick={() => handleShiftMonth(-1)}
                  aria-label="Bulan sebelumnya"
                >
                  <FaChevronLeft />
                </Button>
                <div className="pt-schedule-calendar__month-label">
                  {formatMonthLabel(monthCursor)}
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={() => handleShiftMonth(1)}
                  aria-label="Bulan berikutnya"
                >
                  <FaChevronRight />
                </Button>
              </div>

              <Form
                className="pt-schedule-calendar__filters"
                onSubmit={handleFilterSubmit}
              >
                <Form.Group>
                  <Form.Label>Nama staff</Form.Label>
                  <Form.Control
                    value={filterForm.ptName}
                    onChange={event =>
                      setFilterForm(prev => ({
                        ...prev,
                        ptName: event.target.value,
                      }))
                    }
                    placeholder="Filter staff"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Nama client</Form.Label>
                  <Form.Control
                    value={filterForm.clientName}
                    onChange={event =>
                      setFilterForm(prev => ({
                        ...prev,
                        clientName: event.target.value,
                      }))
                    }
                    placeholder="Filter client"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filterForm.status}
                    onChange={event =>
                      setFilterForm(prev => ({
                        ...prev,
                        status: event.target.value,
                      }))
                    }
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <div className="pt-schedule-calendar__filter-actions">
                  <Button type="submit" variant="primary">
                    Terapkan
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleResetFilters}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={fetchSchedules}
                    disabled={loading}
                  >
                    <FaRedoAlt className="me-2" />
                    Refresh
                  </Button>
                </div>
              </Form>
            </div>

            <div className="pt-schedule-calendar__legend">
              {Object.values(STATUS_META).map(item => (
                <Badge key={item.label} bg={item.badge} className="me-2">
                  {item.label}
                </Badge>
              ))}
            </div>

            {loading ? (
              <div className="pt-schedule-calendar__loading">
                <Spinner animation="border" size="sm" />
                <span>Memuat jadwal session...</span>
              </div>
            ) : null}

            <div className="pt-schedule-calendar__grid-scroll">
              <div className="pt-schedule-calendar__weekday-row">
                {WEEKDAY_LABELS.map(label => (
                  <div key={label} className="pt-schedule-calendar__weekday">
                    {label}
                  </div>
                ))}
              </div>

              <div className="pt-schedule-calendar__grid">
                {calendarDays.map(day => {
                  const isoDate = formatISODate(day);
                  const items = schedulesByDate.get(isoDate) || [];
                  const isCurrentMonth = day.getMonth() === monthCursor.getMonth();
                  const isSelected = isoDate === selectedDate;
                  const isToday = isoDate === todayLabel;

                  return (
                    <button
                      key={isoDate}
                      type="button"
                      className={[
                        'pt-schedule-calendar__day',
                        isCurrentMonth ? '' : 'is-outside',
                        isSelected ? 'is-selected' : '',
                        isToday ? 'is-today' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setSelectedDate(isoDate)}
                    >
                      <div className="pt-schedule-calendar__day-head">
                        <span className="pt-schedule-calendar__day-number">
                          {day.getDate()}
                        </span>
                        {items.length > 0 ? (
                          <Badge bg={isSelected ? 'dark' : 'light'} text={isSelected ? 'light' : 'dark'}>
                            {items.length}
                          </Badge>
                        ) : null}
                      </div>

                      <div className="pt-schedule-calendar__events">
                        {items.slice(0, 3).map(item => {
                          const meta = resolveStatusMeta(item.status);

                          return (
                            <div
                              key={item.id}
                              className={`pt-schedule-calendar__event pt-schedule-calendar__event--${meta.className}`}
                            >
                              <div className="pt-schedule-calendar__event-time">
                                {item.schedule_time}
                              </div>
                              <div className="pt-schedule-calendar__event-pt">
                                {item.pt_name}
                              </div>
                              <div className="pt-schedule-calendar__event-client">
                                {item.client_name}
                              </div>
                            </div>
                          );
                        })}

                        {items.length > 3 ? (
                          <div className="pt-schedule-calendar__more">
                            +{items.length - 3} sesi lagi
                          </div>
                        ) : null}

                        {items.length === 0 ? (
                          <div className="pt-schedule-calendar__empty">
                            {isCurrentMonth ? 'Kosong' : '\u00A0'}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4 mt-1">
          <Col xl={8}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <strong>Agenda {formatLongDate(selectedDate)}</strong>
                  <div className="small text-muted">
                    {selectedDateSchedules.length} jadwal, total sisa sesi client{' '}
                    {selectedDateRemainingSessions}
                  </div>
                </div>
                <Badge bg="light" text="dark">
                  {selectedDate}
                </Badge>
              </Card.Header>

              <Card.Body className="p-0">
                {selectedDateSchedules.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    Belum ada jadwal private session pada tanggal ini.
                  </div>
                ) : (
                  <Table responsive hover className="mb-0">
	                    <thead>
	                      <tr>
	                        <th>Jam</th>
	                        <th>Staff</th>
	                        <th>Client</th>
	                        <th>Status</th>
	                        <th>Sisa sesi</th>
	                        <th>Catatan</th>
                          <th className="text-end">AI</th>
	                      </tr>
	                    </thead>
	                    <tbody>
                      {selectedDateSchedules.map(item => {
                        const meta = resolveStatusMeta(item.status);

                        return (
                          <tr key={item.id}>
                            <td>{item.schedule_time}</td>
                            <td>{item.pt_name}</td>
                            <td>{item.client_name}</td>
                            <td>
                              <Badge bg={meta.badge}>{meta.label}</Badge>
                            </td>
	                            <td>{item.remaining_sessions}</td>
	                            <td>{item.notes || '-'}</td>
                              <td className="text-end">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleAnalyzeSchedule(item)}
                                >
                                  Analisis AI
                                </Button>
                              </td>
	                          </tr>
	                        );
	                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

	          <Col xl={4}>
	            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white">
                <strong>Ringkasan status bulan ini</strong>
              </Card.Header>

              <Card.Body>
                <div className="pt-schedule-calendar__status-stack">
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <div key={key} className="pt-schedule-calendar__status-row">
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg={meta.badge}>{meta.label}</Badge>
                      </div>
                      <strong>{summary.statusCounts[key] || 0}</strong>
                    </div>
                  ))}
                </div>
              </Card.Body>
	            </Card>

	            <Card className="shadow-sm border-0 mt-4">
	              <Card.Header className="bg-white">
	                <strong>Rekomendasi AI scheduling</strong>
	              </Card.Header>

	              <Card.Body>
                  <Form onSubmit={handleAIFormSubmit} className="d-grid gap-3">
                    <Form.Group>
                      <Form.Label>Nama client</Form.Label>
                      <Form.Control
                        value={aiForm.clientName}
                        onChange={event =>
                          setAiForm(previousValue => ({
                            ...previousValue,
                            clientId: 0,
                            clientName: event.target.value,
                          }))
                        }
                        placeholder="Nama client PT"
                      />
                    </Form.Group>

                    <Row className="g-3">
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label>Tanggal target</Form.Label>
                          <Form.Control
                            type="date"
                            value={aiForm.preferredDate}
                            onChange={event =>
                              setAiForm(previousValue => ({
                                ...previousValue,
                                preferredDate: event.target.value,
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group>
                          <Form.Label>Jam target</Form.Label>
                          <Form.Control
                            type="time"
                            value={aiForm.preferredTime}
                            onChange={event =>
                              setAiForm(previousValue => ({
                                ...previousValue,
                                preferredTime: event.target.value,
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group>
                      <Form.Label>Outlet ID</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        value={aiForm.outletId}
                        disabled={aiOutletLocked}
                        onChange={event =>
                          setAiForm(previousValue => ({
                            ...previousValue,
                            outletId: event.target.value,
                          }))
                        }
                        placeholder="Kosongkan jika mengikuti scope akun"
                      />
                      <Form.Text className="text-muted">
                        {aiOutletLocked
                          ? 'Mengikuti outlet akun yang sedang login.'
                          : 'Kosongkan jika ingin mengikuti scope akun yang sedang login.'}
                      </Form.Text>
                    </Form.Group>

                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" variant="primary" disabled={aiLoading}>
                        {aiLoading ? 'Menganalisis...' : 'Analisis jadwal'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => {
                          setAiRecommendation(null);
                          setAiForm({
                            outletId: fallbackOutletId ? String(fallbackOutletId) : '',
                            clientId: 0,
                            clientName: '',
                            preferredDate: selectedDate || formatISODate(new Date()),
                            preferredTime: '',
                          });
                        }}
                      >
                        Reset AI
                      </Button>
                    </div>
                  </Form>

                  {aiRecommendation ? (
                    <div className="mt-4 d-grid gap-3">
                      <div className="border rounded-3 p-3 bg-light-subtle">
                        <div className="small text-muted mb-1">Client terpilih</div>
                        <div className="fw-semibold">{aiRecommendation.client?.full_name || aiForm.clientName || '-'}</div>
                        <div className="small text-muted">
                          Outlet #{aiRecommendation.client?.outlet_id || aiRecommendation.resolved_outlet_id || '-'} •
                          {' '}sisa sesi {aiRecommendation.client?.remaining_sessions ?? 0} •
                          {' '}riwayat {aiRecommendation.client?.total_attendances ?? 0} sesi
                        </div>
                      </div>

                      {aiRecommendation.no_show_prediction ? (
                        <div className="border rounded-3 p-3">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <div>
                              <div className="small text-muted">Prediksi no-show</div>
                              <div className="fw-semibold">
                                Risiko {Math.round(Number(aiRecommendation.no_show_prediction.score || 0))}%
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <Badge bg={resolveRecommendationRiskMeta(aiRecommendation.no_show_prediction.label).badge}>
                                {resolveRecommendationRiskMeta(aiRecommendation.no_show_prediction.label).label}
                              </Badge>
                              <Badge bg="light" text="dark">
                                Keyakinan {resolveRecommendationConfidenceLabel(aiRecommendation.no_show_prediction.confidence)}
                              </Badge>
                            </div>
                          </div>
                          <div className="small text-muted mb-2">
                            {aiRecommendation.no_show_prediction.context_label || 'Prediksi berdasarkan histori client'}
                          </div>
                          <ul className="ps-3 mb-0 small">
                            {(aiRecommendation.no_show_prediction.reasons || []).map(reason => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}

                      <div>
                        <div className="fw-semibold mb-2">Trainer paling cocok</div>
                        <div className="d-grid gap-2">
                          {(aiRecommendation.trainer_recommendations || []).length > 0 ? (
                            aiRecommendation.trainer_recommendations.map(item => (
                              <div key={item.pt_user_id} className="border rounded-3 p-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                  <div className="fw-semibold">{item.pt_name}</div>
                                  <Badge bg="primary">Skor {Math.round(Number(item.score || 0))}</Badge>
                                </div>
                                <div className="small text-muted mb-2">
                                  Tingkat selesai {Math.round(Number(item.completion_rate || 0))}% •
                                  {' '}riwayat client {item.client_history_count || 0}x
                                </div>
                                <ul className="ps-3 mb-0 small">
                                  {(item.reasons || []).map(reason => (
                                    <li key={reason}>{reason}</li>
                                  ))}
                                </ul>
                              </div>
                            ))
                          ) : (
                            <div className="small text-muted">Belum ada trainer yang bisa direkomendasikan.</div>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="fw-semibold mb-2">Slot terbaik</div>
                        <div className="d-grid gap-2">
                          {(aiRecommendation.slot_recommendations || []).length > 0 ? (
                            aiRecommendation.slot_recommendations.map((item, index) => (
                              <div key={`${item.schedule_date}-${item.schedule_time}-${item.pt_user_id}`} className="border rounded-3 p-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                  <div>
                                    <div className="fw-semibold">
                                      #{index + 1} • {formatLongDate(item.schedule_date)}
                                    </div>
                                    <div className="small text-muted">
                                      {item.schedule_time} • {item.pt_name}
                                    </div>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    <Badge bg="primary">Skor {Math.round(Number(item.score || 0))}</Badge>
                                    <Badge bg={resolveRecommendationRiskMeta(item.no_show_risk_label).badge}>
                                      Risiko {resolveRecommendationRiskMeta(item.no_show_risk_label).label}
                                    </Badge>
                                  </div>
                                </div>
                                <ul className="ps-3 mb-0 small">
                                  {(item.reasons || []).map(reason => (
                                    <li key={reason}>{reason}</li>
                                  ))}
                                </ul>
                              </div>
                            ))
                          ) : (
                            <div className="small text-muted">
                              Belum ada slot yang lolos dari conflict check pada horizon pencarian ini.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="small text-muted mt-4">
                      Isi nama client lalu jalankan analisis, atau klik tombol <strong>Analisis AI</strong> pada agenda harian.
                    </div>
                  )}
	              </Card.Body>
	            </Card>

	            <Card className="shadow-sm border-0 mt-4">
	              <Card.Header className="bg-white">
	                <strong>Catatan modul</strong>
	              </Card.Header>

              <Card.Body className="small text-muted">
                <div className="mb-2">
                  Session Schedule sekarang fokus untuk tampilan kalender bulanan.
                </div>
                <div className="mb-2">
                  Pengaturan jadwal dilakukan dari Program Setup > Private Session Setup > tab
                  Session Schedule.
                </div>
                <div>
                  Halaman ini khusus monitor kalender bulanan.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default PTScheduleManager;
